import React, { useEffect, useState, useCallback, useMemo } from "react"
import { useRouteMatch } from "react-router-dom"

import TranscriptView from '../../components/TranscriptView'
import VideoPlayer, { VideoPlayerProps, PlaybackContextProvider } from '../../components/VideoPlayer'
import Loading from '../../components/Loading'

import moment from 'moment'
import prettyBytes from 'pretty-bytes'

import api from '../../api'
import { Job } from '../../data/job'
import { TranscriptData } from '../../data/transcript'

// import { Job } from 'shared/'

import {
  transcriptToAnnotatedText,
  transcriptToWebVTT
} from '../../services/TranscriptTransform'
import { downloadTextFile } from "../../utils"
import useTitle from "../../hooks/useTitle"

interface DownloadButtonsProps {
  jobName: string
  transcript?: TranscriptData
}

const DownloadButtons: React.FC<DownloadButtonsProps> = ({ jobName, transcript }) => {
  const downloadTextTranscript = useCallback(
    (ev: React.MouseEvent) => {
      ev.preventDefault()
      if (!transcript) return

      downloadTextFile(jobName + ".txt", transcript.transcripts[0].transcript)
    },
    [ jobName, transcript ]
  )
  const downloadTimestampTextTranscript = useCallback(
    (ev: React.MouseEvent) => {
      ev.preventDefault()
      if (!transcript) return

      downloadTextFile(jobName + ".txt", transcriptToAnnotatedText(
        transcript.items
      ))
    },
    [ jobName, transcript ]
  )
  const downloadWebVTTTranscript = useCallback(
    (ev: React.MouseEvent) => {
      ev.preventDefault()
      if (!transcript) return

      downloadTextFile(jobName + ".txt", transcriptToWebVTT(
        transcript.items
      ))
    },
    [ jobName, transcript ]
  )

  return <div className="mb-3 text-center">
    <h6 className="mb-1">Download</h6>
    <div className="btn-group">
      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={downloadTextTranscript}
        disabled={!transcript}
      >Text</button>

      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={downloadTimestampTextTranscript}
        disabled={!transcript}
      >Text with timestamps</button>

      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={downloadWebVTTTranscript}
        disabled={!transcript}
      >WebVTT Captions</button>
    </div>
  </div>
}

const JobDetails: React.FC = () => {
  const routeMatch = useRouteMatch<{jobId: string}>("/transcript/jobs/:jobId")
  const jobId = routeMatch?.params.jobId

  const [ job, setJob ] = useState<Job>()
  const [ mediaUrl, setMediaUrl ] = useState<string>()
  const [ transcript, setTranscript ] = useState<TranscriptData>()
  const [ transcriptNotReady, setTranscriptNotReady ] = useState<boolean>(false)

  useTitle(`${job?.job_name || "Job"} | Transcription | Speech2`)

  useEffect(() => {
    if (!jobId) return

    (async () => {
      const jobResp = await api.jobs.get(jobId)
      if (jobResp.data.error !== true) {
        setJob(jobResp.data.data)
      }
    })();

    (async () => {
      const response = await api.jobs.jobMedia(jobId)
      if (response.data.error === false) {
        setMediaUrl(response.data.data.media)
      }
    })();

    (async () => {
      try {
        const response = await api.jobs.jobTranscript(jobId)
        if (response.data.error === false) {
          setTranscript(response.data.data)
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 404) {
            setTranscriptNotReady(true)
            return
          }
        }

        throw error
      }
    })();
  }, [ jobId, setJob, setMediaUrl, setTranscript, setTranscriptNotReady ])

  const webvttCaptions = useMemo(
    () => {
      if (!transcript?.items) return null

      return 'data:text/plain;charset=utf-8,' + encodeURIComponent(transcriptToWebVTT(
        transcript.items
      ))
    },
    [ transcript ]
  )

  if (!jobId) {
    return null
  }

  if (!job) {
    return <Loading />
  }

  let videoPlayer = <Loading />
  if (mediaUrl && job) {
    const tracks: any[] = []

    if (webvttCaptions) {
      tracks.push({
        id: "webvttcaptions",
        kind: "captions",
        src: webvttCaptions,
        default: true,
        mode: "showing",
      })
    }

    const videoJsOptions: VideoPlayerProps = {
      autoplay: false,
      controls: true,
      preload: 'metadata',
      sources: [
        {
          src: mediaUrl,
          type: "video/mp4",
        }
      ],
      tracks,
      fluid: true,
    }

    videoPlayer = <VideoPlayer {...videoJsOptions} />
  }

  let transcribeTime = '-'
  if (job) {
    if (job.finished_at) {
      let transcribeTimeDuration = moment.duration(moment(job.finished_at).diff(moment(job.submitted_at)))
      transcribeTime = transcribeTimeDuration.humanize()
    }
  }

  let transcriptView = <Loading />
  if (transcript) {
    transcriptView = <TranscriptView
      transcript={transcript}
    />
  } else if (transcriptNotReady) {
    transcriptView = <div className="my-4">
      This file is currently being transcribed. We'll notify you when it's ready!
    </div>
  }

  return (
    <PlaybackContextProvider>
      <div className="container-fluid page-container">
        <div className="row my-2">
          <div className="col-md-6 position-fixed-md">
            <h3>{job.job_name}</h3>
            <ul className="my-2 list-group list-group-horizontal-sm justify-content-center">
              <li className="list-group-item text-center">
                <small>File Size</small><br />
                {job.file_metadata ? prettyBytes(parseInt(job.file_metadata.format.size)) : '-'}
              </li>
              <li className="list-group-item text-center">
                <small>Play Time</small><br />
                {job.file_metadata ? moment.duration(parseFloat(job.file_metadata.format.duration), 'seconds').humanize() : '-'}
              </li>
              <li className="list-group-item text-center">
                <small>Time to Transcribe</small><br />
                {transcribeTime}
              </li>
            </ul>

            <DownloadButtons transcript={transcript} jobName={job.job_name} />

            <div className="mb-3">
              { videoPlayer }
            </div>
          </div>
          <div className="col-md-6 offset-md-6">
            { transcriptView }
          </div>
        </div>
      </div>
    </PlaybackContextProvider>
  )
}

export default JobDetails
